import React, { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useField } from 'formik';
import { Colors, AnalyticsProps, useAnalytics } from '@inplayer-org/inplayer-ui';
import noop from 'lodash/noop';

// hooks
import useBoolean from 'hooks/useBoolean';

// components
import Select from './Select';
import ListItem from './ListItem';
import WindowSizeObserver from './WindowSizeObserver';
import DropdownPagination from './DropdownPagination';
import LoadingSpinner from './LoadingSpinner';

const TopContainer = styled.div`
  overflow: hidden;
`;

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
  z-index: 9;
`;

interface ListProps {
  top: number;
  left: number;
  width: number;
  height: string;
}

const ListContainer = styled.div<ListProps>`
  ${({ top, left, width, height }) => `
    display: grid;
    grid-template-rows: 1fr auto;
    position: fixed;
    top: ${top}px;
    left: ${left}px;
    width: ${width}px;
    min-height: 150px;
    height: ${height};
    overflow: hidden;
    z-index: 10;
    border: 1px solid ${Colors.gray};
    background-color: ${Colors.white};
    box-shadow: 0px 2px 6px ${Colors.darkGray};
  `}
`;

const List = styled.ul`
  overflow-y: scroll;
`;

export interface Option extends AnalyticsProps {
  label: string;
  value?: string;
  data?: any;
  isDisabled?: boolean;
  isCurrent?: boolean;
}

interface ListPosition {
  top: number;
  left: number;
  width: number;
}

interface OwnProps {
  name: string;
  defaultOption?: Option;
  options: Option[];
  currentPage?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (pageNumber: number) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  onChangeSelection?: (option: Option) => void;
  height?: string;
}

export const TRACK_TYPE = 'multipage_dropdown';

type Props = OwnProps & AnalyticsProps;

const MultipageDropdown: FC<Props> = ({
  name,
  defaultOption = { label: '' },
  options,
  totalItems = options.length,
  itemsPerPage = 20,
  onPageChange = noop,
  isLoading = false,
  isDisabled = false,
  height = '200px',
  tag,
}) => {
  const { track } = useAnalytics();

  const [{ value: fieldValue }, { error }, { setValue }] = useField<string>(name);

  const { value: isOpen, setTrue: open, setFalse: close } = useBoolean(false);

  const [position, setPosition] = useState<ListPosition>(() => ({ top: 0, left: 0, width: 0 }));

  const [currentOption, listedOptions] = useMemo(() => {
    const _currentOption = options.find(({ isCurrent, value }) => isCurrent || value === fieldValue) || defaultOption;
    const _listedOptions = options.filter((option) => option !== _currentOption);

    return [_currentOption, _listedOptions] as const;
  }, [options, fieldValue, defaultOption]);

  const openClick = useCallback(
    (rect: DOMRect) => {
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });

      if (tag) {
        track({ event: 'click', type: TRACK_TYPE, tag: `${tag}-open` });
      }

      open();
    },
    [tag, track]
  );

  const closeClick = useCallback(() => {
    if (tag) {
      track({ event: 'click', type: TRACK_TYPE, tag: `${tag}-close` });
    }

    close();
  }, [tag, track]);

  const onSelect = useCallback(
    (option: Option) => {
      setValue(option.data ?? option.value);

      if (tag) {
        track({ event: 'click', type: TRACK_TYPE, tag: `${tag}-select` });
      }

      close();
    },
    [setValue, close, tag, track]
  );

  return (
    <TopContainer>
      <Select onClick={openClick} isDisabled={isDisabled || isLoading} error={error}>
        {currentOption.label}
      </Select>
      <WindowSizeObserver onResize={close} />
      {isOpen && (
        <>
          <FullscreenOverlay onClick={closeClick} />
          <ListContainer {...position} height={height}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <List>
                {defaultOption && (
                  <ListItem option={defaultOption} onSelect={onSelect}>
                    {defaultOption.label}
                  </ListItem>
                )}
                {listedOptions.map((option) => (
                  <ListItem
                    key={option.value}
                    option={option}
                    onSelect={option.value === currentOption.value ? closeClick : onSelect}
                    isSelected={option.value === currentOption.value}
                  >
                    {option.label}
                  </ListItem>
                ))}
              </List>
            )}
            <DropdownPagination
              tag={tag}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={onPageChange}
              isDisabled={isLoading}
            />
          </ListContainer>
        </>
      )}
    </TopContainer>
  );
};

export default MultipageDropdown;
